import { timestampNow } from '@bufbuild/protobuf/wkt';
import { create } from '@bufbuild/protobuf';
import { formatTimestamp } from '~/shared/utils/formatTimestamp';
import {
  Property,
  ObjectNode,
  DeviceConfigSourceSchema,
  PropertySchema,
  ObjectNodeSchema,
} from '~/types/wavin/sentio/v1/device_config_source_pb';
import { ValueModel } from './ValueModel';
import { VID } from '~/types/wavin/sentio/v1/broker_pb';
import { DeviceConfigSchema } from '~/types/wavin/blaze/v1/device_config_pb';

export type TableProperty = {
  rawOid?: number;
  rawVid?: number;
  oid?: string;
  vid?: string;
  timestamp: string;
  value?: ValueModel;
  isDeviceUnavailable?: boolean;
  timestampSeconds?: bigint;
};

export type TableObject = {
  oid?: string;
  name?: string;
  deviceType?: string;
  isDeviceUnavailable?: boolean;
  subRows: (TableProperty | TableObject)[];
};

const toHexString = (n?: number): string =>
  n ? `0x${n.toString(16).toUpperCase()}` : '-';

const toVIDString = (vid: number): string =>
  VID[vid]
    ? toHexString(vid).concat(
        ' - ',
        VID[vid].substring(4).toLowerCase().split('_').join(' '),
      )
    : '-';

const isDeviceUnavailable = (properties: Property[]): boolean =>
  !!properties.some(
    // 16388 = 0x4004 - device warning lost communication with periphery
    (property) => property.id === 16388 && property.value.value === 1,
  );

export class BrokerValuesModel {
  readonly data: TableObject;

  constructor(objectTree: ObjectNode) {
    this.data = BrokerValuesModel.createTableData(objectTree);
  }

  static createTableData(node: ObjectNode, parent?: ObjectNode): TableObject;
  static createTableData(node: Property, parent?: ObjectNode): TableProperty;
  static createTableData(
    node: ObjectNode | Property,
    parent?: ObjectNode,
  ): TableObject | TableProperty {
    if (node.$typeName === ObjectNodeSchema.typeName) {
      const objectNode = node as ObjectNode;
      return {
        oid: toHexString(objectNode.id),
        name: objectNode.name,
        deviceType: objectNode.deviceType,
        isDeviceUnavailable: isDeviceUnavailable(objectNode.properties),
        subRows: [
          ...objectNode.children.map((child) =>
            BrokerValuesModel.createTableData(child, objectNode),
          ),
          ...objectNode.properties.map((child) =>
            BrokerValuesModel.createTableData(child, objectNode),
          ),
        ],
      } as TableObject;
    }

    const propertyNode = node as Property;

    return {
      rawOid: parent?.id,
      rawVid: node.id,
      name: parent?.name,
      oid: toHexString(parent?.id),
      vid: toVIDString(propertyNode.id),
      deviceType: parent && parent.deviceType,
      isDeviceUnavailable: parent && isDeviceUnavailable(parent.properties),
      timestamp: formatTimestamp(propertyNode.timestamp),
      timestampSeconds: propertyNode.timestamp?.seconds,
      value: new ValueModel(propertyNode.value),
    } as TableProperty;
  }

  static createDeviceConfigDelta(
    newValue: string,
    property: TableProperty,
    isHexValue: boolean = false,
  ) {
    const timestamp = timestampNow();

    return create(DeviceConfigSchema, {
      timestamp,
      source: {
        case: 'sentioSource',
        value: create(DeviceConfigSourceSchema, {
          changes: [
            create(ObjectNodeSchema, {
              id: property.rawOid,
              properties: [
                create(PropertySchema, {
                  id: property.rawVid,
                  timestamp,
                  valid: true,
                  value: isHexValue
                    ? property.value?.createNewOriginalValueFromHex(newValue)
                    : property.value?.createNewOriginalValue(newValue),
                }),
              ],
            }),
          ],
        }),
      },
    });
  }
}
